import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CrownIcon, MonitorIcon, UsersIcon, CompassIcon, ArrowUpRightIcon, SparklesIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../i18n/translations';
const itTags = ['React', 'Node.js', 'Python', 'Java', 'Go', 'DevOps', 'AWS/GCP', 'QA', 'iOS/Android', 'UI/UX', 'Data Science', 'Scrum Master'];
export function ServicesSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="relative py-20 lg:py-28 bg-white overflow-hidden" id="services" data-id="element-1115" data-mp="22j6pjselkx9u">
      <div className="absolute inset-0 pointer-events-none" data-id="element-1116" data-mp="1nnr2i7cfq1my">
        <div className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-primary-50/50 blur-3xl hidden sm:block" data-id="element-1117" data-mp="1sjpjh9vfohdc" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-cyan-50/40 blur-3xl hidden sm:block" data-id="element-1118" data-mp="2plgd7qnbspgw" />
        <div className="absolute inset-0 opacity-[0.018]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, rgba(15,23,42,1) 1px, transparent 0)`,
        backgroundSize: '48px 48px'
      }} data-id="element-1119" data-mp="2kqkiujum9q0m" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-1120" data-mp="26xbdqvdpbqnw">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-1121" data-mp="21zjo680j4gmv">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-1122" data-mp="1j661sravtiiv">
            {t.services.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-1123" data-mp="3i9w9qep3dcnw">
            {t.services.title}
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-2xl mx-auto" data-id="element-1124" data-mp="3s2tp4byselia">
            {t.services.subtitle}
          </p>
        </motion.div>

        <div className="space-y-6" data-id="element-1125" data-mp="xk30vdwyrdy">
          {/* Featured card — Executive Search */}
          <Link to="/services/executive-search" className="block" data-id="element-1126" data-mp="2zexs165b2rkz">
            <motion.div initial={{
            opacity: 0,
            y: 24
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.55,
            delay: 0.1
          }} className="group relative rounded-2xl overflow-hidden cursor-pointer" data-id="element-1127" data-mp="34d93tmf5vc5u">
              <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900" data-id="element-1128" data-mp="2e0w0e6piyyln" />
              <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-primary-500/10 blur-3xl" data-id="element-1129" data-mp="293mayr24ymxt" />
              <div className="absolute bottom-0 left-1/4 w-[200px] h-[200px] rounded-full bg-cyan-500/8 blur-2xl" data-id="element-1130" data-mp="24aazudqajk03" />
              <div className="absolute inset-0 opacity-[0.04]" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,1) 1px, transparent 0)`,
              backgroundSize: '28px 28px'
            }} data-id="element-1131" data-mp="1ue80ffmtpzgu" />
              <div className="relative p-8 lg:p-10 flex flex-col lg:flex-row lg:items-center gap-8" data-id="element-1132" data-mp="1gef1wy522eyb">
                <div className="flex-1" data-id="element-1133" data-mp="3frj48z0xt049">
                  <div className="flex items-center gap-3 mb-5" data-id="element-1134" data-mp="3fqhucxvnqjo4">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-400 to-primary-600 text-white shadow-xl shadow-primary-500/30" data-id="element-1135" data-mp="3aspsbb1ath67">
                      <CrownIcon className="w-7 h-7" data-id="element-1136" data-mp="2cgn9z6n62k8a" />
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-500/20 text-primary-300 text-xs font-semibold rounded-full border border-primary-500/20" data-id="element-1137" data-mp="2ceas0thes9v8">
                      <SparklesIcon className="w-3 h-3" data-id="element-1138" data-mp="23e474mjds7m1" />
                      {t.services.badge}
                    </span>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-extrabold text-white mb-3" data-id="element-1139" data-mp="1tiaxpc0yku8h">
                    {t.services.card1_title}
                  </h3>
                  <p className="text-navy-300 leading-relaxed max-w-2xl" data-id="element-1140" data-mp="1fs7dsrpjxcm7">
                    {t.services.card1_desc}
                  </p>
                </div>
                <div className="flex-shrink-0 flex flex-row lg:flex-col gap-3" data-id="element-1141" data-mp="15t85ja8p7izq">
                  <div className="px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-center" data-id="element-1142" data-mp="1art7pf7igcqt">
                    <div className="text-2xl font-extrabold text-white" data-id="element-1143" data-mp="r4ydfobozwy4">
                      {t.services.card1_stat1_val}
                    </div>
                    <div className="text-xs text-navy-400 mt-0.5" data-id="element-1144" data-mp="n5pn6tfv2h7b">
                      {t.services.card1_stat1_label}
                    </div>
                  </div>
                  <div className="px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-center" data-id="element-1145" data-mp="w3jhjyu2f0ru">
                    <div className="text-2xl font-extrabold text-primary-400" data-id="element-1146" data-mp="1a317win0em3b">
                      {t.services.card1_stat2_val}
                    </div>
                    <div className="text-xs text-navy-400 mt-0.5" data-id="element-1147" data-mp="1jtvvbqbzrrqc">
                      {t.services.card1_stat2_label}
                    </div>
                  </div>
                </div>
                <ArrowUpRightIcon className="absolute top-6 right-6 w-5 h-5 text-navy-500 group-hover:text-primary-400 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" data-id="element-1148" data-mp="1tscomtyekifk" />
              </div>
            </motion.div>
          </Link>

          {/* Featured card — IT Recruitment */}
          <Link to="/services/it-recruitment" className="block" data-id="element-1149" data-mp="20v9pmjil1ztu">
            <motion.div initial={{
            opacity: 0,
            y: 24
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.55,
            delay: 0.18
          }} className="group relative rounded-2xl overflow-hidden cursor-pointer" data-id="element-1150" data-mp="20tglbavfzrp0">
              <div className="absolute inset-0 bg-gradient-to-br from-violet-900 via-violet-800 to-navy-900" data-id="element-1151" data-mp="2tk6xrtt2yg8v" />
              <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-violet-500/12 blur-3xl" data-id="element-1152" data-mp="u7cndm1t0cb9" />
              <div className="absolute bottom-0 left-1/4 w-[200px] h-[200px] rounded-full bg-primary-500/8 blur-2xl" data-id="element-1153" data-mp="z5e440brbzjk" />
              <div className="absolute inset-0 opacity-[0.04]" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,1) 1px, transparent 0)`,
              backgroundSize: '28px 28px'
            }} data-id="element-1154" data-mp="rnga74626x1g" />
              <div className="relative p-8 lg:p-10 flex flex-col lg:flex-row lg:items-center gap-8" data-id="element-1155" data-mp="cz3bhzfl7039">
                <div className="flex-1" data-id="element-1156" data-mp="1ak7qr5u7uy1t">
                  <div className="flex items-center gap-3 mb-5" data-id="element-1157" data-mp="1aj6cytltfxge">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-400 to-violet-600 text-white shadow-xl shadow-violet-500/30" data-id="element-1158" data-mp="15mz6mzpicjxx">
                      <MonitorIcon className="w-7 h-7" data-id="element-1159" data-mp="vngomot5r88i" />
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-violet-500/20 text-violet-300 text-xs font-semibold rounded-full border border-violet-500/20" data-id="element-1160" data-mp="3ldtzk1wy612a">
                      <SparklesIcon className="w-3 h-3" data-id="element-1161" data-mp="9xidoq3x3ilz" />
                      {t.services.badge}
                    </span>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-extrabold text-white mb-3" data-id="element-1162" data-mp="exwje1i1qggu">
                    {t.services.card2_title}
                  </h3>
                  <p className="text-violet-200/70 leading-relaxed max-w-2xl" data-id="element-1163" data-mp="4xm4mcgd50zk">
                    {t.services.card2_desc}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-1.5" data-id="element-1164" data-mp="so9pdybdnl3j">
                    {itTags.map(tag => <span key={tag} className="text-[10px] font-semibold px-2 py-0.5 bg-violet-500/15 text-violet-300 rounded-md border border-violet-500/20" data-id="element-1165" data-mp="xoxh6m7fk5yt">
                        {tag}
                      </span>)}
                  </div>
                </div>
                <div className="flex-shrink-0 flex flex-row lg:flex-col gap-3" data-id="element-1166" data-mp="1gdknt95993ei">
                  <div className="px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-center" data-id="element-1167" data-mp="3137unw9dlbn9">
                    <div className="text-2xl font-extrabold text-white" data-id="element-1168" data-mp="2w56ebj3c3qfh">14</div>
                    <div className="text-xs text-violet-300/70 mt-0.5" data-id="element-1169" data-mp="3atj8qbmrx17v">
                      днів у середньому
                    </div>
                  </div>
                  <div className="px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-center" data-id="element-1170" data-mp="35uy6k6nyo7gs">
                    <div className="text-2xl font-extrabold text-violet-400" data-id="element-1171" data-mp="2i53z64mevb3x">
                      50+
                    </div>
                    <div className="text-xs text-violet-300/70 mt-0.5" data-id="element-1172" data-mp="2s31mmpwg53vh">
                      технологічних стеків
                    </div>
                  </div>
                </div>
                <ArrowUpRightIcon className="absolute top-6 right-6 w-5 h-5 text-violet-500/50 group-hover:text-violet-400 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" data-id="element-1173" data-mp="2n6b661gqc69j" />
              </div>
            </motion.div>
          </Link>

          {/* Two cards row — secondary services */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-stretch" data-id="element-1174" data-mp="33fxj28wtpq5g">
            {/* Team building */}
            <Link to="/services/team-building" className="block" data-id="element-1175" data-mp="2yo739nf4aa6n">
              <motion.div initial={{
              opacity: 0,
              y: 24
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.28
            }} className="group relative p-7 rounded-2xl bg-white border border-navy-100 hover:border-primary-200 hover:shadow-xl hover:shadow-primary-100/50 transition-all duration-300 cursor-pointer overflow-hidden h-full" data-id="element-1176" data-mp="2ymmbg5k6t5ch">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 to-cyan-400 rounded-t-2xl" data-id="element-1177" data-mp="8xu0ixlhenny" />
                <div className="absolute inset-0 bg-gradient-to-br from-primary-50/0 to-primary-50/0 group-hover:from-primary-50/40 group-hover:to-transparent transition-all duration-300 rounded-2xl" data-id="element-1178" data-mp="lhqxt3mx6n30" />
                <div className="relative" data-id="element-1179" data-mp="v8ll8bbwjsq1">
                  <div className="flex items-start justify-between mb-5" data-id="element-1180" data-mp="v7aj0fy3ut11">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-white shadow-lg shadow-primary-500/25 group-hover:shadow-xl group-hover:shadow-primary-500/35 transition-shadow duration-300" data-id="element-1181" data-mp="3tqr7008mnwox">
                      <UsersIcon className="w-6 h-6" data-id="element-1182" data-mp="ci3yqohnxtd4" />
                    </div>
                    <ArrowUpRightIcon className="w-4 h-4 text-navy-300 group-hover:text-primary-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" data-id="element-1183" data-mp="1ixwr27840db1" />
                  </div>
                  <h3 className="text-lg font-bold text-navy-900 mb-2 group-hover:text-primary-700 transition-colors" data-id="element-1184" data-mp="3jzcqds1yeu0n">
                    {t.services.card3_title}
                  </h3>
                  <p className="text-sm text-navy-500 leading-relaxed" data-id="element-1185" data-mp="3t0al1jqi5p6k">
                    {t.services.card3_desc}
                  </p>
                  <div className="mt-5 p-3 rounded-xl bg-primary-50/60 border border-primary-100/60" data-id="element-1186" data-mp="3p00di2n2aqpl">
                    <div className="text-lg font-extrabold text-primary-700" data-id="element-1187" data-mp="31a645v1ybka3">
                      {t.services.card3_case_val}
                    </div>
                    <div className="text-[11px] text-primary-500 font-medium" data-id="element-1188" data-mp="3a1omabigka5t">
                      {t.services.card3_case_label}
                    </div>
                  </div>
                </div>
              </motion.div>
            </Link>

            {/* Career consulting */}
            <Link to="/services/career-consulting" className="block" data-id="element-1189" data-mp="2hj12czf7716b">
              <motion.div initial={{
              opacity: 0,
              y: 24
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.36
            }} className="group relative p-7 rounded-2xl bg-white border border-navy-100 hover:border-amber-200 hover:shadow-xl hover:shadow-amber-100/50 transition-all duration-300 cursor-pointer overflow-hidden h-full" data-id="element-1190" data-mp="2djafv7wpf77l">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-amber-400 rounded-t-2xl" data-id="element-1191" data-mp="2c7dtz1okn6ia" />
                <div className="absolute inset-0 bg-gradient-to-br from-amber-50/0 to-amber-50/0 group-hover:from-amber-50/40 group-hover:to-transparent transition-all duration-300 rounded-2xl" data-id="element-1192" data-mp="2h9cw688krxdn" />
                <div className="relative" data-id="element-1193" data-mp="15q0ewfi1y7x2">
                  <div className="flex items-start justify-between mb-5" data-id="element-1194" data-mp="1lzrj2o8km4a5">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/25 group-hover:shadow-xl group-hover:shadow-amber-500/35 transition-shadow duration-300" data-id="element-1195" data-mp="1h8j65i8180s8">
                      <CompassIcon className="w-6 h-6" data-id="element-1196" data-mp="17c6e0os4xuzl" />
                    </div>
                    <ArrowUpRightIcon className="w-4 h-4 text-navy-300 group-hover:text-amber-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" data-id="element-1197" data-mp="24wsky06tvo9v" />
                  </div>
                  <h3 className="text-lg font-bold text-navy-900 mb-2 group-hover:text-amber-700 transition-colors" data-id="element-1198" data-mp="1uycy49eu3166">
                    {t.services.card4_title}
                  </h3>
                  <p className="text-sm text-navy-500 leading-relaxed" data-id="element-1199" data-mp="jlk56vab46oj">
                    {t.services.card4_desc}
                  </p>
                  <div className="mt-5 flex items-center gap-2" data-id="element-1200" data-mp="eo0rpxlfnxxn">
                    <div className="flex -space-x-1.5" data-id="element-1201" data-mp="2qam73sstfe81">
                      {['bg-amber-400', 'bg-orange-400', 'bg-rose-400'].map((c, i) => <div key={i} className={`w-6 h-6 rounded-full ${c} border-2 border-white`} data-id="element-1202" data-mp="306ximgkl001f" data-mpi={i} />)}
                    </div>
                    <span className="text-xs text-navy-500 font-medium" data-id="element-1203" data-mp="39zd0zezsiqmu">
                      {t.services.card4_count}
                    </span>
                  </div>
                </div>
              </motion.div>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}